/* eslint-disable no-console */
import delay from 'lodash/delay'
import has from 'lodash/has'
import React, { FC, memo, MutableRefObject, useCallback, useEffect, useRef, useState } from 'react'
import compare from 'react-fast-compare'
import toast from 'react-hot-toast'
import { connect } from 'react-redux'

import useAccount from 'src/hooks/useAccount'
import useDispatcher from 'src/hooks/useDispatcher'
import useFetch from 'src/hooks/useFetch'
import useLocalStorage from 'src/hooks/useLocalStorage'
import useProductMatchParams from 'src/hooks/useProductMatchParams'
import useQueryParams from 'src/hooks/useQueryParams'
import {
  ComponentsSettingsActionsProps,
  resetComponentsSettings,
  setComponentsSettingsLoading,
} from 'src/redux/actions/componentsSettingsActions'
import { RootState } from 'src/redux/store'
import useDashboardsService from 'src/services/dashboardService'
import useFreezedTagItemsStore from 'src/stores/useFreezedTagItemsStore'
import useMenusStore from 'src/stores/useMenusStore'
import useSelectedDashboardStore from 'src/stores/useSelectedDashboardStore'
import { FetchAppMenuResponseInterface } from 'src/types/AppMenuInterface'
import { ReactCallBackFunction } from 'src/types/CallbackFunction'
import SaveDashboardItemInterface from 'src/types/SaveDashboardItemInterface'
import { getApiUrl } from 'src/utils'
import { prepareComponentTree } from 'src/utils/configManager'
import { FROM_SMART_ANALYSIS_KEY } from 'src/utils/constants/CrossLaunching'
import { Currency } from 'src/utils/currency'
import { getDefaultDashboardFn } from 'src/utils/dashboardsUtils'
import { Export } from 'src/utils/export'
import { findSubProductOnMenusFn } from 'src/utils/menuUtils'
import { Metric, MetricChartData } from 'src/utils/metric'
import { SubProduct } from 'src/utils/product'
import { Market } from 'src/utils/queries'
import { getDashboardKeyFn } from 'src/utils/saveDashboardUtils'
import { ComponentsSettings } from 'src/utils/settings'
import { isNeedToClearQueryFn } from 'src/utils/urlUtils'

export type Dashboard = {
  id: string
  label: string
  favorite: boolean
  selected: boolean
  sub_product?: string
  components_settings?: ComponentsSettings
}
export type DashboardsObject = { [pageCode: string]: SaveDashboardItemInterface } // Keep favorites and selecteds dashboards by subproduct
export type DimensionColor = { id: string; color: string; dimension: string }
export type UpdateMarketFn = (market: Market, persist?: boolean) => Promise<void>

interface GetFavoriteDashboardSettingsArgs {
  subProduct: string
  enableLoading?: boolean
  resetSettings?: boolean
  programId?: string
  menus: FetchAppMenuResponseInterface
  dashboardParamId?: string
}
export interface SettingsCtx {
  isDashboardListLoading: boolean
  components: object
  currencies: ReadonlyArray<any>
  dimensionsColors: ReadonlyArray<DimensionColor>
  market: Market
  componentsSettingsRef: MutableRefObject<ComponentsSettings>
  exports: ReadonlyArray<Export>
  currencyUpdate: boolean
  dashboardsList: SaveDashboardItemInterface[]
  favoritesDashboards: DashboardsObject
  selectedsDashboards: DashboardsObject
  exportConfig: object
  /** Common metrics data (e.g: in KeyMetrics, MultiCharts...). One source of data and changes in one place & simplify the use from the different components */
  metricsData: Metric[]
  chartsData: MetricChartData[]
  setExportConfig: (value: object) => void
  setFavoritesDashboards: (value: DashboardsObject) => void
  setSelectedsDashboards: (value: DashboardsObject) => void
  setDashboardsList: ReactCallBackFunction<SaveDashboardItemInterface[]>
  setCurrencyUpdate: (value: boolean) => void
  /** Set common metrics (e.g: in KeyMetrics, MultiCharts...). One source of data and changes in one place & simplify the use from the different components */
  setMetricsData: ReactCallBackFunction<Metric[]>
  setChartsData: (value: MetricChartData[]) => void
  getComponents: () => Promise<any>
  getCurrencies: () => Promise<any>
  getDimensionsColors: () => Promise<any>
  updateSelectedsDashboards: (value: DashboardsObject) => void
  updateMarket: UpdateMarketFn
  fetchExports: () => Promise<any>
  deleteExport: (id: string) => Promise<any>
  updateExport: (id: string, settings: any) => Promise<any>
  getDefaultDashboardToLoad: (args: GetFavoriteDashboardSettingsArgs) => Promise<any>
  clearQueryParams: (queryParams?: object) => void
}

export const SettingsContext = React.createContext<SettingsCtx>({} as SettingsCtx)

const SettingsProvider: FC<{ [x: string]: any } & ComponentsSettingsActionsProps> = ({
  children,
  settings,
  oldSettings,
}) => {
  const [dimensionsColors, setDimensionsColors] = useLocalStorage('dimensions-colors')
  const { setQueryParams, queryParams } = useQueryParams()
  const { getDashboard } = useDashboardsService()
  const [currencies, setCurrencies] = useState<ReadonlyArray<Currency>>()
  const [currencyUpdate, setCurrencyUpdate] = useState<boolean>(false)
  const [components, setComponents] = useState<object>()
  const [market, setMarket] = useState<Market>()
  const [exportConfig, setExportConfig] = useState({})
  const [metricsData, setMetricsData] = useState<Metric[]>([])
  const [chartsData, setChartsData] = useState<MetricChartData[]>([])
  const [exports, setExports] = useState<ReadonlyArray<Export>>()
  const [dashboardsList, setDashboardsList] = useState<SaveDashboardItemInterface[]>([])
  const [favoritesDashboards, setFavoritesDashboards] = useState<DashboardsObject>({})
  const [selectedsDashboards, setSelectedDashboards] = useState<DashboardsObject>({})
  const { informations: accountInformations } = useAccount()
  const componentsSettingsRef = useRef<undefined | ComponentsSettings>()
  const setSelectedDashboard = useSelectedDashboardStore(s => s.setSelectedDashboard)

  const routeMatch = useProductMatchParams()

  const setFreezedTagItems = useFreezedTagItemsStore(s => s.setFreezedTagItems)

  const isMarketIntel = routeMatch?.params?.subProduct === SubProduct.AnalyticsV2
  const program =
    isMarketIntel && routeMatch?.params?.program ? parseInt(routeMatch.params.program) : undefined

  const resetComponentsSettingsDispatcher = useDispatcher(resetComponentsSettings)
  const setComponentsSettingsLoadingDispatcher = useDispatcher(setComponentsSettingsLoading)

  const setMenus = useMenusStore(s => s.setMenus)

  const [, executeGetComponents] = useFetch<any>(
    {
      url: `${getApiUrl()}/api/admin/components_v3`,
      method: 'GET',
    },
    { manual: true },
  )

  const [, executeGetCurrencies] = useFetch<any>(
    {
      url: `${getApiUrl()}/api/currencies`,
      method: 'GET',
    },
    { manual: true },
  )

  const [, executeGetDimensionsColors] = useFetch<any>(
    {
      // To replace with right endpoint once deployed on QA
      url: `${getApiUrl()}/api/admin/dimension_colors`,
      method: 'GET',
    },
    { manual: true },
  )

  const [, executeSetDefaultMarket] = useFetch({ method: 'POST' }, { manual: true })
  const [, executeFetchExports] = useFetch<ReadonlyArray<Export>>(
    { url: `${getApiUrl()}/api/admin/export_configs` },
    { manual: true },
  )
  const [, executeDeleteExport] = useFetch({ method: 'DELETE' }, { manual: true })
  const [, executeUpdateExport] = useFetch({ method: 'PUT' }, { manual: true })

  const fetchExports = async () => {
    const { data: exports } = await executeFetchExports()
    setExports(exports)
  }

  const deleteExport = async (id: string) => {
    await executeDeleteExport({ url: `${getApiUrl()}/api/admin/export_configs/${id}` })
    await fetchExports()
    toast.success('Export successfully deleted')
  }

  const updateExport = async (id: string, settings: Partial<Export>) => {
    await executeUpdateExport({
      url: `${getApiUrl()}/api/admin/export_configs/${id}`,
      data: { export_config: settings },
    })
    await fetchExports()
    toast.success('Export successfully updated')
  }

  const getDimensionsColors = useCallback(async () => {
    const { data: dimensionsColors } = await executeGetDimensionsColors()

    setDimensionsColors(
      dimensionsColors.map(
        (data: { resource_id: string; hexa_code: string; dimension: string }) => ({
          id: data.resource_id,
          color: data.hexa_code,
          dimension: data.dimension,
        }),
      ),
    )
  }, [executeGetDimensionsColors, setDimensionsColors])

  const [, executeGetExportConfig] = useFetch<any>({ method: 'GET' }, { manual: true })

  /**
   * Update selected dashboards by sub product
   */
  const updateSelectedDashboards = useCallback(async (value: DashboardsObject) => {
    setSelectedDashboards(s => ({ ...s, ...value }))
  }, [])

  /**
   * Clear queries params in url
   */
  const clearQueryParams = useCallback(
    (queryParams?: object) => {
      setQueryParams({
        ...queryParams,
        scopes: { filters: [] },
        time_period: {},
        dimensions: '',
      })
    },
    [setQueryParams],
  )

  const settingsRef = useRef(settings)
  const oldSettingsRef = useRef(oldSettings)

  useEffect(() => {
    settingsRef.current = settings
    oldSettingsRef.current = oldSettings
  }, [settings, oldSettings])

  /**
   * Get favorite dashboard via api by product and update store
   *
   * On the "Standard Dashboard", this method is not called, we use the parameters of the url or default data to build the settings
   *
   * @param {string} subProductCodeArg
   * @param {boolean} enableLoading
   * @param {boolean} reset
   */
  const getDefaultDashboardToLoad = useCallback(
    async ({
      subProduct: subProductCodeArg,
      enableLoading = false,
      resetSettings: reset = false,
      programId: programArg,
      menus,
    }: GetFavoriteDashboardSettingsArgs): Promise<any> => {
      const subProductCode = subProductCodeArg || SubProduct.Dashboard
      const dashboardKey = getDashboardKeyFn({ subProductCode, programId: programArg })
      enableLoading && setComponentsSettingsLoadingDispatcher(true)
      let responseData: any
      const exportId = queryParams.export_id
      const from = queryParams?.from
      const dashboardId = queryParams?.dashboard_id

      const programId = programArg || program

      if (exportId) {
        // If "export_id" is present in url, get settings from "export_config" api response
        // Display "PDF" only
        const { data: configIdSettings }: any = await executeGetExportConfig(
          {
            url: `${getApiUrl()}/api/admin/export_configs/${exportId}`,
          },
          undefined,
        )

        setExportConfig(configIdSettings)
        responseData = configIdSettings
        reset = true
      } else {
        // Normal process
        const subproduct = findSubProductOnMenusFn({ menus, subProductCode })

        if (!subproduct) {
          return
        }

        const defaultDashboardToLoad = getDefaultDashboardFn({
          dashboards: subproduct.dashboards || [],
          dashboardParamId: dashboardId,
          from: queryParams?.from,
        })

        if (defaultDashboardToLoad) {
          const { data } = await getDashboard({
            id: defaultDashboardToLoad.id,
            programId: defaultDashboardToLoad.program_id,
            subProductCode: subproduct.code,
          })
            .then(response => {
              setSelectedDashboard(defaultDashboardToLoad)
              return response
            })
            .catch(() => {
              console.error('Error while fetching favorite dashboard settings')
              return { data: { components_settings: {} } }
            })
          responseData = data
        } else {
          responseData = {
            components_settings: {},
          }
        }
      }

      const { components_settings, program_id } = responseData ?? { components_settings: {} }

      let newSettings: ComponentsSettings = settingsRef.current
      let newOldSettings: ComponentsSettings = oldSettingsRef.current

      const isComponentSettingProgram = programId
        ? program_id?.toString() === programId.toString()
        : true

      if (reset) {
        // Reset settings/oldSettings
        newSettings = { [dashboardKey]: components_settings?.[subProductCode] }
        newOldSettings = { [dashboardKey]: components_settings?.[subProductCode] }
      } else {
        // Keep a page in state even if you switch between products
        // Keep settings if subProductCode key already exist in settings object
        // and merge with new data
        if (
          !has(newSettings, dashboardKey) &&
          has(components_settings, subProductCode) &&
          isComponentSettingProgram
        ) {
          newSettings = { ...newSettings, [dashboardKey]: components_settings[subProductCode] }
          newOldSettings = {
            ...newOldSettings,
            [dashboardKey]: components_settings[subProductCode],
          }
        }
      }

      if (isNeedToClearQueryFn(from) || from === FROM_SMART_ANALYSIS_KEY) {
        if (from === FROM_SMART_ANALYSIS_KEY && reset) {
          const currentDashboardConfig = newSettings[dashboardKey]
          if (currentDashboardConfig) {
            newSettings = {
              ...newSettings,
              [dashboardKey]: {
                ...currentDashboardConfig,
                filterBar: undefined,
              },
            }
            oldSettings = newSettings
          }
        }
        resetComponentsSettingsDispatcher(newSettings, newOldSettings)
      }

      enableLoading && delay(() => setComponentsSettingsLoadingDispatcher(false), 600)
    },
    [
      executeGetExportConfig,
      queryParams.export_id,
      queryParams?.from,
      queryParams?.dashboard_id,
      program,
      resetComponentsSettingsDispatcher,
      setComponentsSettingsLoadingDispatcher,
      setFreezedTagItems,
    ],
  )

  const getComponents = useCallback(async () => {
    const { data: components } = await executeGetComponents(undefined, undefined)
    if (components) {
      // Menus are the same tree: menu_v2 is no longer called.
      setMenus({ menus: components, loading: false, error: null })
      setComponents(prepareComponentTree(components))
    }
    return components
  }, [executeGetComponents, setMenus])

  const getCurrencies = useCallback(async () => {
    const { data: currencies } = await executeGetCurrencies(undefined, undefined)
    setCurrencies(currencies)
  }, [executeGetCurrencies])

  const updateMarket: UpdateMarketFn = useCallback(
    async (market, persist = true) => {
      try {
        if (persist) {
          await executeSetDefaultMarket({
            url: `${getApiUrl()}/api/benchmark/change_default_program`,
            data: {
              program_id: market.program?.id,
              seller_category: market.program.seller_category,
            },
          })
        }

        setMarket(market)
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    [executeSetDefaultMarket],
  )

  useEffect(() => {
    if (!market && accountInformations) {
      setMarket({
        program: {
          id: accountInformations.default_program,
          seller_category: accountInformations.seller_category,
        },
      })
    }
  }, [accountInformations, market])

  const contextValue = {
    components,
    currencies,
    dimensionsColors: dimensionsColors || [],
    market,
    componentsSettingsRef,
    exports,
    currencyUpdate,
    dashboardsList,
    setCurrencyUpdate,
    setDashboardsList,
    setMetricsData,
    metricsData,
    chartsData,
    setChartsData,
    favoritesDashboards,
    selectedsDashboards,
    setSelectedsDashboards: setSelectedDashboards,
    setFavoritesDashboards,
    getComponents,
    getCurrencies,
    getDimensionsColors,
    updateMarket,
    deleteExport,
    updateExport,
    fetchExports,
    getDefaultDashboardToLoad,
    updateSelectedsDashboards: updateSelectedDashboards,
    clearQueryParams,
    exportConfig,
    setExportConfig,
    isDashboardListLoading: false,
  }

  return (
    <SettingsContext.Provider value={contextValue as SettingsCtx}>
      {children}
    </SettingsContext.Provider>
  )
}

const mapStateToProps = (state: RootState) => ({
  settings: state.componentsSettings.settings,
  oldSettings: state.componentsSettings.oldSettings,
})

export default connect(mapStateToProps, null)(memo(SettingsProvider, compare))
